import React, { useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth, type UserType } from 'context/authentication/useAuth'
import { useLeftNavStore } from './left-nav-store'
import { SlideOutNavButton } from './SlideOutNavButton'
import { cn } from 'components/UI/cn'
import type { NavSection } from './types'

interface SlideOutNavItemProps {
  label: string
  path: string
  section?: NavSection
  onClick?: () => void
  /** When true, the slide-out panel stays open after clicking this item (default: false) */
  keepOpen?: boolean
  /** Minimum user type required to see this item (default: 'Standard') */
  minimumRole?: UserType
}

export const SlideOutNavItem: React.FC<SlideOutNavItemProps> = ({
  label,
  path,
  section,
  onClick,
  keepOpen = false,
  minimumRole = 'Standard',
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAllowed } = useAuth()
  const { setLastVisited, closeSlideOut } = useLeftNavStore()

  if (!isAllowed(minimumRole)) return null

  const isActive =
    location.pathname === path || location.pathname.startsWith(`${path}/`)

  const handleClick = useCallback(() => {
    navigate(path)
    // Track the last visited path for this section
    if (section) {
      setLastVisited(section, path)
    }
    // Close the slide-out after navigation unless keepOpen is true
    if (!keepOpen) {
      closeSlideOut()
    }
    onClick?.()
  }, [
    navigate,
    path,
    section,
    setLastVisited,
    closeSlideOut,
    onClick,
    keepOpen,
  ])

  return (
    <li className="m-0 p-0">
      <SlideOutNavButton
        onClick={handleClick}
        isActive={isActive}
        aria-current={isActive ? 'page' : undefined}
      >
        <span
          className={cn(
            'text-[13px] text-[#191d23] leading-[1.4]',
            isActive ? 'font-medium' : 'font-light'
          )}
        >
          {label}
        </span>
      </SlideOutNavButton>
    </li>
  )
}
